import { CampaignState, ListCampaignResponseItem } from '@/api';
import { BadgeState } from '@/primer/components';
import { MS_PER_HOUR, parseDateMs, pluralize } from '@/utils';

export function deriveCampaignCardState(campaign: ListCampaignResponseItem, now: number) {
  const state = campaign.state ?? '';
  const hasClaimed = (campaign.userClaimedProductsCount ?? 0) > 0;

  const isAvailable = state === CampaignState.Available;
  const isParticipating = state === CampaignState.Participating;

  const needsToSelectOrClaim = isParticipating && !hasClaimed;
  const isClaimedAndParticipating = isParticipating && hasClaimed;
  const isUnclaimedInvitation = isAvailable || (isParticipating && !hasClaimed);

  // The list API never returns COMPLETED programs, so "all done" comes from the task counts.
  const totalTaskCount = campaign.totalTaskCount ?? 0;
  const allTasksDone = totalTaskCount > 0 && (campaign.completedTaskCount ?? 0) >= totalTaskCount;
  const isCompleted =
    state === CampaignState.Completed || (isClaimedAndParticipating && allTasksDone);

  // The backend keeps tasks locked until an INSTANT_OPT_IN product ships.
  const isAwaitingShipment =
    isClaimedAndParticipating &&
    !isCompleted &&
    campaign.campaignType === 'INSTANT_OPT_IN' &&
    campaign.shipmentState !== 'SHIPPED' &&
    campaign.shipmentState !== 'CANCELLED';

  const invitationExpiryMs = campaign.invitationExpiresAt
    ? parseDateMs(campaign.invitationExpiresAt)
    : null;
  const isInvitationExpired = invitationExpiryMs !== null && invitationExpiryMs <= now;
  const hoursLeft =
    invitationExpiryMs !== null && !isInvitationExpired
      ? Math.ceil((invitationExpiryMs - now) / MS_PER_HOUR)
      : null;
  const isInviteExpiredUnclaimed = isUnclaimedInvitation && isInvitationExpired;

  const badgeNumber = isUnclaimedInvitation && hoursLeft !== null ? hoursLeft : undefined;
  const badgeLabel = isCompleted
    ? 'Completed'
    : isInviteExpiredUnclaimed
      ? 'Expired'
      : badgeNumber !== undefined
        ? `${pluralize(badgeNumber, 'hr')} left`
        : undefined;
  const badgeState = isCompleted
    ? BadgeState.SELECTED
    : isInviteExpiredUnclaimed
      ? BadgeState.OVERDUE
      : badgeNumber !== undefined
        ? BadgeState.DAYS_LEFT
        : undefined;

  const ctaLabel = isCompleted
    ? 'Program Completed'
    : isClaimedAndParticipating
      ? isAwaitingShipment
        ? 'View Program'
        : 'Post Content'
      : isInviteExpiredUnclaimed
        ? 'Invitation Expired'
        : 'View Program';
  const ctaVariant: 'primary' | 'secondary' =
    isCompleted || (isClaimedAndParticipating && !isAwaitingShipment) ? 'secondary' : 'primary';
  const ctaDisabled = isInviteExpiredUnclaimed;

  const imageUrl = campaign.imageUrl ?? '/assets/images/placeholder.svg';
  const imageAlt: string | undefined = campaign.imageUrl ? (campaign.name ?? undefined) : '';
  const tags = [
    BadgeState.DELIVERED,
    ...(campaign.hasSocialTasks ? [BadgeState.REVIEW_SOCIAL] : []),
  ];

  return {
    isAvailable,
    needsToSelectOrClaim,
    isCompleted,
    isClaimedAndParticipating,
    isAwaitingShipment,
    productName: campaign.name ?? '',
    imageUrl,
    imageAlt,
    tags,
    badgeNumber,
    badgeLabel,
    badgeState,
    ctaLabel,
    ctaVariant,
    ctaDisabled,
  };
}
